<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();

const {
  content,
  contentAttributes,
  id: messageId,
  conversationId,
} = useMessageContext();
const accountId = useMapGetter('getCurrentAccountId');
const attrs = computed(() => contentAttributes.value || {});
const replyText = ref('');
const isSending = ref(false);

const action = async (name, text = '') => {
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${messageId.value}/mercado_livre_actions`,
      { ml_action: name, text }
    );
    if (name === 'answer') replyText.value = '';
    useAlert(t('INBOX_MGMT.ADD.MERCADO_LIVRE.ACTION_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.ADD.MERCADO_LIVRE.ACTION_ERROR')
    );
  }
};

const sendReply = () => {
  if (!replyText.value.trim() || isSending.value) return;
  isSending.value = true;
  action('answer', replyText.value.trim()).finally(() => {
    isSending.value = false;
  });
};
</script>

<template>
  <div
    class="flex max-w-md flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-800 shadow-sm"
  >
    <div
      class="flex items-center border-b border-slate-700 bg-slate-900 px-3 py-2"
    >
      <Icon icon="i-lucide-store" size="16" class="mr-2 text-yellow-400" />
      <span class="text-xs font-medium text-slate-300">
        {{ t('INBOX_MGMT.ADD.MERCADO_LIVRE.QUESTION_CONTEXT') }}
      </span>
      <span
        class="ml-auto text-[11px] font-semibold uppercase tracking-wide text-slate-500"
      >
        {{ t('INBOX_MGMT.ADD.MERCADO_LIVRE.PRODUCT') }}
      </span>
    </div>

    <img
      v-if="attrs.image_url"
      :src="attrs.image_url"
      :alt="
        attrs.mercado_livre_item_title ||
        t('INBOX_MGMT.ADD.MERCADO_LIVRE.PRODUCT_ALT')
      "
      class="h-44 w-full object-cover"
      loading="lazy"
    />

    <div class="p-3">
      <p
        v-if="attrs.mercado_livre_item_title"
        class="mb-2 text-sm font-semibold text-slate-200"
      >
        {{ attrs.mercado_livre_item_title }}
      </p>
      <p class="whitespace-pre-wrap text-sm text-slate-100">{{ content }}</p>
    </div>

    <div class="flex items-center gap-2 border-t border-slate-700 p-3">
      <input
        v-model="replyText"
        type="text"
        class="min-w-0 flex-1 rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        :placeholder="t('INBOX_MGMT.ADD.MERCADO_LIVRE.REPLY_PLACEHOLDER')"
        maxlength="2000"
        @keyup.enter="sendReply"
      />
      <button
        type="button"
        class="rounded bg-yellow-500 px-3 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
        :disabled="!replyText.trim() || isSending"
        @click="sendReply"
      >
        {{ t('INBOX_MGMT.ADD.MERCADO_LIVRE.REPLY') }}
      </button>
    </div>

    <div class="flex border-t border-slate-700">
      <button
        type="button"
        class="flex-1 px-3 py-2 text-xs text-slate-400 hover:text-white"
        @click="action('hide')"
      >
        {{ t('INBOX_MGMT.ADD.MERCADO_LIVRE.HIDE') }}
      </button>
      <button
        type="button"
        class="flex-1 border-l border-slate-700 px-3 py-2 text-xs text-red-300 hover:text-red-200"
        @click="action('delete')"
      >
        {{ t('INBOX_MGMT.ADD.MERCADO_LIVRE.DELETE') }}
      </button>
    </div>
  </div>
</template>
